import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Folder, FileVideo } from 'lucide-react';
import { useAuth } from '@features/auth';
import {
  FileCard,
  useFiles,
  useFileActions,
  type File,
} from '@features/files';
import { FolderCard, useFolders, type Folder as FolderType } from '@features/folders';

function getGreetingKey(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export function HomePage() {
  const { t } = useTranslation(['common', 'files', 'folders']);
  const { user } = useAuth();
  const { data: recentFiles } = useFiles({ limit: 4 });
  const { data: rootFolders } = useFolders({ parentId: null, limit: 4 });
  // Contagem total de pastas em todos os níveis (raiz + subpastas) para o card
  // de resumo. `limit: 1` busca só o `meta.total`, sem carregar a lista inteira.
  const { data: folderCount } = useFolders({ flat: true, limit: 1 });

  const { handlers, dialogs } = useFileActions();

  const files = recentFiles?.data || [];
  const folders = rootFolders?.data || [];

  // Totais reais vindos do backend (não o tamanho das listas de preview, que
  // são limitadas a 4). `meta.total` de `recentFiles` já conta arquivos de
  // todos os níveis; `folderCount` conta pastas de todos os níveis.
  const totalFiles = recentFiles?.meta?.total ?? files.length;
  const totalFolders = folderCount?.meta?.total ?? folders.length;
  const firstName = user?.name?.split(' ')[0] ?? '';
  const isAdmin = user?.role === 'admin';

  const greeting = t(`common:home.greeting.${getGreetingKey()}`);

  const fileCanWrite = (file: File) => isAdmin || file.uploadedBy === user?.id;
  const folderCanWrite = (folder: FolderType) =>
    isAdmin || folder.createdBy === user?.id;

  return (
    <div className="space-y-8">
      {/* Hero / Greeting */}
      <header className="pt-2">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
          {firstName ? `${greeting}, ${firstName}` : t('common:home.welcome')}
        </h1>
        <p className="mt-2 text-sm text-foreground-muted max-w-2xl">
          {t('common:home.subtitle')}
        </p>
      </header>

      {/* Stats */}
      <section
        aria-label={t('common:home.stats')}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted">
              {t('folders:title')}
            </p>
            <p className="mt-1 text-2xl font-semibold text-foreground tabular-nums">
              {totalFolders}
            </p>
          </div>
          <Folder className="h-6 w-6 text-foreground-subtle" aria-hidden="true" />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted">
              {t('files:title')}
            </p>
            <p className="mt-1 text-2xl font-semibold text-foreground tabular-nums">
              {totalFiles}
            </p>
          </div>
          <FileVideo className="h-6 w-6 text-foreground-subtle" aria-hidden="true" />
        </div>
      </section>

      {/* Root folders */}
      {folders.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">{t('folders:title')}</h2>
            <Link
              to="/files"
              className="text-sm font-medium text-brand-primary cursor-pointer hover:text-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 rounded"
            >
              {t('folders:viewAll')}
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {folders.map((folder) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                canWrite={folderCanWrite(folder)}
                onShare={handlers.handleShareFolder}
                onDownload={handlers.handleDownloadFolder}
              />
            ))}
          </div>
        </section>
      )}

      {/* Recent files */}
      {files.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">{t('files:recentFiles')}</h2>
            <Link
              to="/files"
              className="text-sm font-medium text-brand-primary cursor-pointer hover:text-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 rounded"
            >
              {t('files:viewAll')}
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {files.map((file) => (
              <FileCard
                key={file.id}
                file={file}
                canWrite={fileCanWrite(file)}
                onPlay={handlers.handlePlayFile}
                onDownload={handlers.handleDownloadFile}
                onShare={handlers.handleShareFile}
                onDelete={handlers.handleDeleteFile}
              />
            ))}
          </div>
        </section>
      )}

      {dialogs}
    </div>
  );
}
